import { rolesRanks } from "./roles-ranks";
import { NormalizedRole } from "./types";
import useViewerRole from "./use-viewer-role";

type Options = {
  strict?: boolean;
};

/**
 * Check if the user has the minimum right in the current shop.
 * By defaults, it checks if they have at least the provided
 * right, but you can set the `strict` option to `true` to
 * check if they have exactly the provided right.
 *
 * This utility accepts a NormalizedRole as the minimum right,
 * because the old API did not use the normalized roles
 * and it could sometimes be unclear.
 *
 * Usage:
 *
 * const canDoThis = useViewerHasRightInCurrentShop("admin");
 */
function useViewerHasMinimumRight(
  minimumRight: NormalizedRole,
  options: Options = { strict: false },
) {
  const viewerRole = useViewerRole();

  if (!viewerRole) return false;

  const minimumRanks = rolesRanks[minimumRight];
  const viewerRanks = rolesRanks[viewerRole];

  if (options.strict) {
    return viewerRanks === minimumRanks;
  }

  return viewerRanks >= minimumRanks;
}

export function useViewerHasAtLeastAdminRights() {
  return useViewerHasMinimumRight("admin");
}

export function useViewerHasAtLeastStoreRights() {
  return useViewerHasMinimumRight("store");
}

export function useViewerHasAtLeastCustomerSupportRights() {
  return useViewerHasMinimumRight("customersupport");
}

export function useViewerHasAtLeastLogisticianRights() {
  return useViewerHasMinimumRight("logistician");
}

export function useViewerHasAtLeastOwnerRights() {
  return useViewerHasMinimumRight("owner");
}
